import React, { useState } from 'react';
import { mockDistributionRules, distributionRoles, reportSections } from '../../data/mockData';

const DistributionRules = () => {
  // State for rules list
  const [rules, setRules] = useState(mockDistributionRules);
  const [selectedRule, setSelectedRule] = useState(null);
  
  // State for form
  const [formData, setFormData] = useState({
    name: '',
    role: 'Executive',
    recipients: [],
    includedSections: []
  });
  
  // State for email input
  const [emailInput, setEmailInput] = useState('');
  
  // State for modals
  const [showTestModal, setShowTestModal] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [showWarning, setShowWarning] = useState(false);

  // Show success toast
  const showSuccessToast = (message) => {
    setToastMessage(message);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  // Handle rule selection
  const handleSelectRule = (rule) => {
    setSelectedRule(rule);
    setFormData({
      name: rule.name,
      role: rule.role,
      recipients: [...rule.recipients],
      includedSections: [...rule.includedSections]
    });
    setShowWarning(false);
  };

  // Handle new rule
  const handleNewRule = () => {
    setSelectedRule(null);
    setFormData({
      name: '',
      role: 'Executive',
      recipients: [],
      includedSections: []
    });
    setEmailInput('');
    setShowWarning(false);
  };

  // Handle section toggle
  const handleSectionToggle = (sectionName) => {
    setFormData(prev => {
      const sections = prev.includedSections.includes(sectionName)
        ? prev.includedSections.filter(s => s !== sectionName)
        : [...prev.includedSections, sectionName];
      return { ...prev, includedSections: sections };
    });
    setShowWarning(false);
  };

  // Handle adding email
  const handleAddEmail = (e) => {
    e.preventDefault();
    const email = emailInput.trim().toLowerCase();
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (email && emailRegex.test(email) && !formData.recipients.includes(email)) {
      setFormData(prev => ({
        ...prev,
        recipients: [...prev.recipients, email]
      }));
      setEmailInput('');
    }
  };

  // Handle removing email
  const handleRemoveEmail = (email) => {
    setFormData(prev => ({
      ...prev,
      recipients: prev.recipients.filter(e => e !== email)
    }));
  };

  // Handle key press for email input
  const handleEmailKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddEmail(e);
    }
  };

  // Handle save
  const handleSave = () => {
    if (!formData.name.trim()) {
      alert('Please enter a rule name');
      return;
    }

    if (formData.recipients.length === 0) {
      alert('Please add at least one recipient');
      return;
    }

    if (formData.includedSections.length === 0) {
      setShowWarning(true);
      return;
    }

    if (selectedRule) {
      // Update existing rule
      setRules(prev => prev.map(r => 
        r.id === selectedRule.id 
          ? { ...r, ...formData }
          : r
      ));
      showSuccessToast('Distribution rule updated successfully!');
    } else {
      // Create new rule
      const newRule = {
        id: String(Date.now()),
        ...formData,
        isActive: true,
        createdAt: new Date().toISOString()
      };
      setRules(prev => [...prev, newRule]);
      setSelectedRule(newRule);
      showSuccessToast('Distribution rule created successfully!');
    }
  };

  // Handle test rule
  const handleTestRule = () => {
    if (formData.includedSections.length === 0) {
      setShowWarning(true);
      return;
    }
    setShowTestModal(true);
  };

  // Get role description
  const getRoleDescription = (roleName) => {
    const role = distributionRoles.find(r => r.name === roleName);
    return role?.description || '';
  };

  return (
    <div className="flex h-[calc(100vh-200px)] min-h-[600px]">
      {/* Success Toast */}
      {showToast && (
        <div className="fixed top-4 right-4 z-50 animate-slide-in">
          <div className="bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg flex items-center space-x-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span>{toastMessage}</span>
          </div>
        </div>
      )}

      {/* Left Panel - Rule List */}
      <div className="w-80 border-r border-slate-200 bg-slate-50 flex flex-col">
        <div className="p-4 border-b border-slate-200 bg-white">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-slate-900">Distribution Rules</h3>
            <button
              onClick={handleNewRule}
              className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
              title="New Rule"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </button>
          </div>
          <p className="text-xs text-slate-500">Control which sections are sent to which roles</p>
        </div>
        
        <div className="flex-1 overflow-y-auto p-3 space-y-2">
          {rules.map((rule) => (
            <div
              key={rule.id}
              onClick={() => handleSelectRule(rule)}
              className={`p-4 rounded-xl cursor-pointer transition-all duration-200 ${
                selectedRule?.id === rule.id
                  ? 'bg-indigo-100 border-2 border-indigo-500 shadow-md'
                  : 'bg-white border border-slate-200 hover:border-indigo-300 hover:shadow-sm'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h4 className="font-medium text-slate-900 text-sm">{rule.name}</h4>
                  <div className="flex items-center space-x-2 mt-1">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      rule.role === 'Executive' 
                        ? 'bg-blue-100 text-blue-700' 
                        : 'bg-purple-100 text-purple-700'
                    }`}>
                      {rule.role}
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      rule.isActive ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'
                    }`}>
                      {rule.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              </div>
              <div className="mt-2 text-xs text-slate-500">
                {rule.recipients.length} recipient{rule.recipients.length !== 1 ? 's' : ''} • {rule.includedSections.length} section{rule.includedSections.length !== 1 ? 's' : ''}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Right Panel - Rule Editor */}
      <div className="flex-1 flex flex-col bg-white">
        <div className="p-6 border-b border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-slate-900">
                {selectedRule ? 'Edit Rule' : 'New Rule'}
              </h2>
              <p className="text-sm text-slate-500 mt-1">
                Configure report distribution settings
              </p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={handleTestRule}
                className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors flex items-center space-x-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                <span>Test Rule</span>
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center space-x-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span>Save Rule</span>
              </button>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="max-w-3xl space-y-8">
            {/* Rule Name */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Rule Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Enter distribution rule name"
                className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
              />
            </div>

            {/* Role Selection */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Role
                <span className="ml-2 text-xs text-slate-400 font-normal">(Select target audience)</span>
              </label>
              <select
                value={formData.role}
                onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
                className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors bg-white"
              >
                {distributionRoles.map((role) => (
                  <option key={role.id} value={role.name}>
                    {role.name}
                  </option>
                ))}
              </select>
              <p className="text-xs text-slate-500 mt-2 flex items-start space-x-1">
                <svg className="w-4 h-4 text-indigo-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>{getRoleDescription(formData.role)}</span>
              </p>
              <div className="mt-2 p-3 bg-indigo-50 border border-indigo-100 rounded-lg">
                <p className="text-xs text-indigo-700 flex items-start space-x-2">
                  <svg className="w-4 h-4 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                  <span>
                    <strong>AI Summarization:</strong> Content will be automatically tailored and summarized based on the selected role. 
                    Executives receive concise summaries, while Compliance Officers get detailed regulatory information.
                  </span>
                </p>
              </div>
            </div>

            {/* Recipient List - Multi-email Tag Input */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Recipient List <span className="text-red-500">*</span>
                <span className="ml-2 text-xs text-slate-400 font-normal">(Press Enter to add email)</span>
              </label>
              
              <div className="border border-slate-300 rounded-xl p-3 focus-within:ring-2 focus-within:ring-indigo-500 focus-within:border-indigo-500 transition-all">
                {/* Email Tags */}
                <div className="flex flex-wrap gap-2 mb-2">
                  {formData.recipients.map((email) => (
                    <span
                      key={email}
                      className="inline-flex items-center px-3 py-1 bg-indigo-100 text-indigo-800 rounded-full text-sm"
                    >
                      <svg className="w-4 h-4 mr-1 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      {email}
                      <button
                        onClick={() => handleRemoveEmail(email)}
                        className="ml-2 text-indigo-600 hover:text-indigo-800"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </span>
                  ))}
                </div>
                
                {/* Email Input */}
                <div className="flex">
                  <input
                    type="email"
                    value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                    onKeyPress={handleEmailKeyPress}
                    placeholder="Enter email address and press Enter"
                    className="flex-1 outline-none text-sm bg-transparent"
                  />
                  <button
                    onClick={handleAddEmail}
                    className="px-3 py-1 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors text-sm font-medium"
                  >
                    Add
                  </button>
                </div>
              </div>
              
              {formData.recipients.length === 0 && (
                <p className="text-xs text-slate-400 mt-2">No recipients added yet</p>
              )}
            </div>

            {/* Included Sections */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Included Sections
                <span className="ml-2 text-xs text-slate-400 font-normal">(Select sections to include in the report)</span>
              </label>
              
              {/* Warning */}
              {showWarning && (
                <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start space-x-2">
                  <svg className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <div>
                    <p className="text-sm font-medium text-amber-800">No sections selected</p>
                    <p className="text-xs text-amber-600">Please select at least one section to include in the distribution.</p>
                  </div>
                </div>
              )}
              
              <div className="grid grid-cols-1 gap-2">
                {reportSections.map((section) => {
                  const isSelected = formData.includedSections.includes(section.name);
                  return (
                    <div
                      key={section.id}
                      onClick={() => handleSectionToggle(section.name)}
                      className={`p-3 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                        isSelected
                          ? 'border-indigo-500 bg-indigo-50'
                          : 'border-slate-200 hover:border-slate-300 bg-white'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                            isSelected ? 'border-indigo-500 bg-indigo-500' : 'border-slate-300'
                          }`}>
                            {isSelected && (
                              <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </div>
                          <div>
                            <span className="font-medium text-slate-900">{section.name}</span>
                            <p className="text-xs text-slate-500">{section.description}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Selected sections summary */}
              {formData.includedSections.length > 0 && (
                <div className="mt-4 p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-500 mb-2">Selected sections ({formData.includedSections.length}):</p>
                  <div className="flex flex-wrap gap-2">
                    {formData.includedSections.map((sectionName) => (
                      <span
                        key={sectionName}
                        className="inline-flex items-center px-2 py-1 bg-indigo-100 text-indigo-800 rounded text-xs"
                      >
                        {sectionName}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Test Rule Modal - Email Preview */}
      {showTestModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between bg-gradient-to-r from-slate-50 to-blue-50">
              <div>
                <h3 className="text-lg font-bold text-slate-900">Email Preview</h3>
                <p className="text-sm text-slate-500">Preview of email content structure</p>
              </div>
              <button
                onClick={() => setShowTestModal(false)}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {/* Email Header Preview */}
              <div className="bg-slate-50 rounded-xl p-4 mb-6">
                <div className="space-y-3">
                  <div className="flex items-center">
                    <span className="text-xs text-slate-500 w-16">From:</span>
                    <span className="text-sm text-slate-900">reports@company.com</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-xs text-slate-500 w-16">To:</span>
                    <div className="flex-1 flex flex-wrap gap-1">
                      {formData.recipients.map((email) => (
                        <span key={email} className="text-sm text-slate-900 bg-white px-2 py-0.5 rounded border border-slate-200">
                          {email}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center">
                    <span className="text-xs text-slate-500 w-16">Subject:</span>
                    <span className="text-sm font-medium text-slate-900">
                      {formData.role === 'Executive' ? 'Executive Report Summary' : 'Compliance Report'} - {formData.name}
                    </span>
                  </div>
                </div>
              </div>

              {/* Email Body Preview */}
              <div className="border border-slate-200 rounded-xl overflow-hidden">
                <div className="bg-indigo-600 text-white p-4">
                  <h4 className="font-semibold">Report Distribution</h4>
                  <p className="text-indigo-100 text-sm">{formData.name}</p>
                </div>
                
                <div className="p-4 space-y-4">
                  <p className="text-slate-700">
                    Dear {formData.role},
                  </p>
                  <p className="text-slate-600 text-sm">
                    Please find below the report sections tailored for your role.
                  </p>
                  
                  {/* Sections Preview */}
                  <div className="space-y-3 mt-4">
                    <h5 className="font-semibold text-slate-700 text-sm uppercase tracking-wide">Included Sections</h5>
                    {formData.includedSections.map((sectionName, index) => {
                      const section = reportSections.find(s => s.name === sectionName);
                      return (
                        <div key={sectionName} className="flex items-start space-x-3 p-3 bg-slate-50 rounded-lg">
                          <span className="w-6 h-6 bg-indigo-500 text-white rounded text-xs flex items-center justify-center font-medium flex-shrink-0">
                            {index + 1}
                          </span>
                          <div>
                            <h6 className="font-medium text-slate-900">{sectionName}</h6>
                            <p className="text-sm text-slate-500">{section?.description}</p>
                            <p className="text-xs text-slate-400 mt-1 italic">
                              [AI-generated content will appear here based on {formData.role} role]
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                
                <div className="bg-slate-50 p-4 border-t border-slate-200">
                  <p className="text-xs text-slate-500 text-center">
                    This is an automated report generated by AI Reporting Engine
                  </p>
                </div>
              </div>

              {/* Role-based info */}
              <div className="mt-4 p-3 bg-blue-50 border border-blue-100 rounded-lg">
                <p className="text-xs text-blue-700 flex items-start space-x-2">
                  <svg className="w-4 h-4 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>
                    Content will be customized for <strong>{formData.role}</strong> role. 
                    {formData.role === 'Executive' 
                      ? ' Executives will receive concise, action-oriented summaries.'
                      : ' Compliance Officers will receive detailed regulatory information and compliance notes.'}
                  </span>
                </p>
              </div>
            </div>

            <div className="p-4 border-t border-slate-200 bg-slate-50">
              <button
                onClick={() => setShowTestModal(false)}
                className="w-full px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
              >
                Close Preview
              </button>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes slide-in {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        .animate-slide-in {
          animation: slide-in 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default DistributionRules;
